// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * `budget-gauge` — pure cost-split computation for the cycle dashboard
 * (PRD §Modules `budget-gauge` M11; issue `fe-cost-consumers`, US §13).
 *
 * Contract (PRD §Interfaces / issue "What to build"):
 *   - input: the already-fetched snapshot's nested `Spend` (PRD §M5) —
 *     already-fetched data, this module never fetches (INV-3).
 *   - output: the input/output cost split, degrading to "unavailable"
 *     rather than throwing or fabricating a figure.
 *
 * The "unavailable vs real zero" split discrimination is no longer
 * re-derived here — `Spend.split_available` travels on the wire
 * (single source, INV-3) and `computeCostSplit` reads it directly.
 */

/** The subset of `CycleSnapshotDto` this module needs: the cumulative cost-so-far `Spend` (PRD §M5). */
export interface BudgetGaugeSnapshot {
  spend: Spend | null | undefined
}

/**
 * Input/output LLM cost split derived from the snapshot's ledger
 * `Spend`.
 */
export interface CostSplitReport {
  /**
   * Mirror of `Spend.split_available` (INV-3 single source) — `false`
   * when the split cannot be trusted (old ComputeEngine capture gap:
   * real calls happened but no per-direction rate was captured). The
   * UI renders "—" in that case, never a fabricated $0.00.
   */
  available: boolean
  /** USD input-side cost — `null` when the split is unavailable. */
  promptCost: number | null
  /** USD output-side cost — `null` when the split is unavailable. */
  completionCost: number | null
  /** Input token count — `null` when unavailable or not a positive figure. */
  promptTokens: number | null
  /** Output token count — `null` when unavailable or not a positive figure. */
  completionTokens: number | null
}

const UNAVAILABLE_SPLIT: CostSplitReport = {
  available: false,
  promptCost: null,
  completionCost: null,
  promptTokens: null,
  completionTokens: null,
}

/**
 * Derive the input/output cost split (US cost-ventilation). Pure-module
 * contract: already-fetched data in, degrade-to-null out, no fetch (INV-3).
 *
 * Reads `Spend.split_available` straight off the wire instead of
 * re-deriving the "unavailable vs real zero" rule locally — the
 * ledger's `LlmSpend.split_available()` (PRD §Interfaces M1) is the
 * single source of that discrimination (INV-3).
 */
export const computeCostSplit = (
  snapshot: BudgetGaugeSnapshot | null | undefined,
): CostSplitReport => {
  const spend = snapshot?.spend
  if (spend == null || !spend.split_available) {
    return UNAVAILABLE_SPLIT
  }

  return {
    available: true,
    promptCost: spend.prompt_cost,
    completionCost: spend.completion_cost,
    promptTokens: spend.prompt_tokens > 0 ? spend.prompt_tokens : null,
    completionTokens:
      spend.completion_tokens > 0 ? spend.completion_tokens : null,
  }
}
